import { Card, NdBadge } from "@/components/Kpi";
import { eurShort, isND, num, perc } from "@/lib/format";
import { usePalette } from "@/lib/palette";

const R = 92;
const CX = 110;
const CY = 110;
const START = -180; // semicerchio: da 180° a 0°
const MAX_RATIO = 1.0; // fondo scala = 100% del budget (obiettivo)

const polar = (angleDeg, radius) => {
  const a = (angleDeg * Math.PI) / 180;
  return [CX + radius * Math.cos(a), CY + radius * Math.sin(a)];
};

const arc = (fromRatio, toRatio, radius, width) => {
  const a1 = START + Math.min(fromRatio, MAX_RATIO) / MAX_RATIO * 180;
  const a2 = START + Math.min(toRatio, MAX_RATIO) / MAX_RATIO * 180;
  const [x1, y1] = polar(a1, radius);
  const [x2, y2] = polar(a2, radius);
  const [x3, y3] = polar(a2, radius - width);
  const [x4, y4] = polar(a1, radius - width);
  const large = Math.abs(a2 - a1) > 180 ? 1 : 0;
  return `M ${x1} ${y1} A ${radius} ${radius} 0 ${large} 1 ${x2} ${y2} L ${x3} ${y3} A ${radius - width} ${radius - width} 0 ${large} 0 ${x4} ${y4} Z`;
};

/** Misuratore radiale: 100% = budget (obiettivo), lancetta sulla previsione di chiusura. */
export default function BudgetGauge({ titolo, sottotitolo, budget, proiezione, scostamentoPerc,
  testId, colore, tipo, etichette = {}, footer = null, compatto = false, neutro = false, soloValore = false }) {
  const lbl = { proiezione: "Proiezione", budget: "Budget obiettivo", scostamento: "Scostamento", ...etichette };
  const palette = usePalette();
  const isLiq = tipo ? tipo === "LIQUIDATO" : colore === palette.liquidato;
  const isPremi = tipo === "PREMI";
  const col = colore || (isLiq ? palette.liquidato : palette.caricato);
  const boxCls = isPremi
    ? "!border-2 !border-sky-500 bg-gradient-to-b from-sky-50/70 to-white"
    : isLiq
      ? "!border-2 bd-liquidato soft-liquidato"
      : "!border-2 bd-caricato soft-caricato";
  const titleCls = isPremi ? "text-sky-700 font-bold"
    : isLiq ? "txt-liquidato font-bold" : "txt-caricato font-bold";
  if (isND(budget) || !budget) {
    return (
      <Card className={`p-6 h-full flex flex-col ${boxCls}`} data-testid={testId}>
        <div className={`uppercase text-xs tracking-[0.18em] ${titleCls}`}>{titolo}</div>
        <div className="flex-1 grid place-items-center py-10"><NdBadge label="budget non impostato" /></div>
      </Card>
    );
  }
  const ratio = isND(proiezione) ? null : proiezione / budget;
  const perc100 = ratio === null ? null : ratio * 100;
  const needleAngle = START + Math.min(ratio ?? 0, MAX_RATIO) / MAX_RATIO * 180;
  const [nx, ny] = polar(needleAngle, R - 24);
  const stato = ratio === null ? "nd" : scostamentoPerc >= 0 ? "positivo"
    : scostamentoPerc >= -10 ? "warning" : "negativo";
  // lancetta, percentuale e proiezione seguono il semaforo dello scostamento
  const statoColore = { positivo: "#16A34A", warning: "#F59E0B",
    negativo: "#DC2626", nd: "#94A3B8" }[stato];

  return (
    <Card className={`${compatto ? "px-5 py-3" : "p-6"} h-full ${neutro ? "" : boxCls}`} data-testid={testId}>
      <div className="flex items-baseline justify-between gap-2">
        <div className={`uppercase text-xs tracking-[0.18em] ${neutro ? "font-semibold text-slate-500" : titleCls}`}>{titolo}</div>
        {soloValore && (
          <div className="text-right leading-tight" data-testid={`${testId}-valore`}>
            <div><span className="text-[10px] uppercase tracking-wider text-slate-400 mr-2">{lbl.proiezione}</span>
              <span className="font-mono text-sm font-semibold text-slate-900">{eurShort(etichette.valore_testata ?? proiezione)}</span></div>
            {etichette.valore_testata !== undefined && (
              <div><span className="text-[10px] uppercase tracking-wider text-slate-400 mr-2">Proiezione</span>
                <span className="font-mono text-sm font-semibold" style={{ color: statoColore }}>{eurShort(proiezione)}</span></div>
            )}
          </div>
        )}
      </div>
      {sottotitolo && <div className="text-xs text-slate-400 mt-1">{sottotitolo}</div>}
      <div className={`flex justify-center ${compatto ? "-mt-2 mb-2" : "mt-2"}`}>
        <svg viewBox={compatto ? "-8 0 236 158" : "0 0 236 158"} className={`w-full ${compatto ? "max-w-[210px]" : "max-w-[280px]"}`}>
          <path d={arc(0, MAX_RATIO, R, 18)} fill="#F1F5F9" />
          <path d={arc(0, 1, R, 18)} fill={col} opacity="0.18" />
          {ratio !== null && <path d={arc(0, ratio, R, 18)} fill={statoColore} opacity="0.9" />}
          {/* tacca obiettivo 100% a fondo scala */}
          <line x1={polar(START + 180, R + 4)[0]} y1={polar(START + 180, R + 4)[1]}
            x2={polar(START + 180, R - 22)[0]} y2={polar(START + 180, R - 22)[1]}
            stroke="#0F172A" strokeWidth="2" />
          <text x={CX + R - 9} y={CY + 16} textAnchor="middle" style={{ fontSize: 10, fill: "#0F172A", fontWeight: 600, fontFamily: "JetBrains Mono, monospace" }}>{eurShort(budget)}</text>
          {ratio !== null && (
            <>
              <line x1={CX} y1={CY} x2={nx} y2={ny} stroke="#0F172A" strokeWidth="3" strokeLinecap="round" />
              <circle cx={CX} cy={CY} r="6" fill="#0F172A" />
            </>
          )}
          <text x={CX} y={CY + 40} textAnchor="middle"
            style={{ fontSize: compatto ? 16 : 24, fontWeight: 700, fill: statoColore, fontFamily: "JetBrains Mono, monospace" }}>
            {perc100 === null ? "n/d" : `${num(perc100, 2)}%`}
          </text>
        </svg>
      </div>
      {!soloValore && (
      <div className="mt-2 grid grid-cols-3 gap-2 text-center">
        <div>
          <div className="text-[10px] uppercase tracking-wider text-slate-400">{lbl.proiezione}</div>
          <div className="font-mono text-sm font-semibold" style={{ color: statoColore }}>{eurShort(proiezione)}</div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-wider text-slate-400">{lbl.budget}</div>
          <div className="font-mono text-sm font-semibold text-slate-500">{eurShort(budget)}</div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-wider text-slate-400">{lbl.scostamento}</div>
          <div className={`font-mono text-sm font-semibold ${stato === "positivo" ? "text-emerald-600" : stato === "warning" ? "text-amber-600" : "text-red-600"}`}>
            {perc(scostamentoPerc)}
          </div>
        </div>
      </div>
      )}
      {footer}
    </Card>
  );
}
